// src/pages/TaxGroupList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Snackbar, Alert, TextField,
  Pagination, Stack, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { getAllTributaryGroups, deleteTributaryGroup } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormTaxGroup from './FormTaxGroup';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';
import useFeedbackAlert from './common/useFeedbackAlert';

function TaxGroupList() {
  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);
  const [items, setItems] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  const [confirmId, setConfirmId] = useState(null);
  const [snack, setSnack] = useState({ open:false, msg:'', sev:'success' });
  const [form, setForm] = useState({ open: false, id: null });
  const { showApiError, FeedbackUi } = useFeedbackAlert();

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('descricao', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    try {
      const { data } = await getAllTributaryGroups({ indice: pagina, qtde, termo, ordenar: orderBy, direcao: order });
      setItems(data.items ?? []);
      setTotalPaginas(data.totalPages ?? 1);
      setTotalItens(data.totalItems ?? 0);
    } catch {
      setSnack({ open:true, msg:'Erro ao carregar grupos', sev:'error' });
    }
  }, [pagina, qtde, termo, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  const sorted = items;

  const onBuscar = () => { setPagina(1); setTermo(searchText); };
  const onKeyDown = (e) => e.key === 'Enter' && onBuscar();

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnack({ open: true, msg, sev: 'success' });
    carregar();
  };

  const excluir = async () => {
    try {
      await deleteTributaryGroup(confirmId);
      setSnack({ open:true, msg:'Grupo excluído', sev:'success' });
      setConfirmId(null);
      if (items.length === 1 && pagina > 1) setPagina(pagina - 1);
      else carregar();
    } catch (e) {
      setConfirmId(null);
      showApiError(e, 'Não foi possível excluir o grupo tributário.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Grupos tributários"
        subtitle="Regras de tributação aplicadas aos produtos na emissão fiscal."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
            Novo grupo
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display:'flex', gap:2, flexWrap:'wrap', alignItems:'center' }}>
        <TextField
          size="small" placeholder="Buscar por descrição..." value={searchText}
          onChange={(e)=>setSearchText(e.target.value)} onKeyDown={onKeyDown}
          InputProps={{
            startAdornment:<SearchIcon sx={{ mr:1, color:'text.secondary' }} />,
            endAdornment: !!searchText && <IconButton size="small" onClick={()=>setSearchText('')}><CloseIcon fontSize="small"/></IconButton>
          }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />
        <Button variant="outlined" onClick={onBuscar}>Buscar</Button>
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="ID" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum grupo tributário encontrado
                </TableCell>
              </TableRow>
            ) : sorted.map(g => (
              <TableRow key={g.id} hover sx={{ cursor: 'pointer' }} onClick={() => abrirForm(g.id)}>
                <TableCell data-label="ID">{g.id}</TableCell>
                <TableCell data-label="Descrição">{g.descricao}</TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar"><IconButton color="primary" onClick={() => abrirForm(g.id)}><EditIcon/></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton color="error" onClick={()=>setConfirmId(g.id)}><DeleteIcon/></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={1} sx={{ mt:2, alignItems:'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e,p)=>setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItens} grupos (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar grupo tributário' : 'Novo grupo tributário'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <FormTaxGroup
          key={form.id || 'novo'}
          embedded
          taxGroupId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <Dialog open={!!confirmId} onClose={()=>setConfirmId(null)}>
        <DialogTitle>Excluir Grupo Tributário</DialogTitle>
        <DialogContent><DialogContentText>Confirmar exclusão?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmId(null)}>Cancelar</Button>
          <Button color="error" onClick={excluir}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <FeedbackUi />
      <Snackbar open={snack.open} autoHideDuration={snack.sev === 'error' ? 8000 : 4000} onClose={()=>setSnack(s=>({...s, open:false}))} anchorOrigin={{vertical:'bottom', horizontal:'right'}}>
        <Alert severity={snack.sev} onClose={()=>setSnack(s=>({...s, open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
export default TaxGroupList;
